import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Checkbox,
  CircularProgress,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Fab,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
  InputAdornment,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DeleteForever as DeleteForeverIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Security as SecurityIcon,
  NetworkCheck as NetworkIcon,
  Dns as DnsIcon,
  Link as LinkIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Block as BlockIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import api, { AllowListEntry, AllowListCreateRequest, AllowListUpdateRequest, AllowListStats } from '../services/api';
import { useTenant } from '../contexts/TenantContext';
import { useTimezone } from '../contexts/TimezoneContext';
import { formatUiDateOnly } from '../utils/dateUtils';
import { useNotification } from '../contexts/NotificationContext';
import { usePermissions } from '../contexts/PermissionContext';
import { ALLOW_LIST_STATUS_COLORS, ALLOW_LIST_TYPE_COLORS } from '../types/allowList';

interface AllowListProps {
  tenantId?: string;
  isSuperUser?: boolean;
}

const AllowList: React.FC<AllowListProps> = ({ tenantId, isSuperUser = false }) => {
  const theme = useTheme();
  const { timezone } = useTimezone();
  const { selectedTenant } = useTenant();
  const { showNotification } = useNotification();
  const { hasPermission } = usePermissions();
  
  const [entries, setEntries] = useState<AllowListEntry[]>([]);
  const [stats, setStats] = useState<AllowListStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<AllowListEntry | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkActing, setBulkActing] = useState(false);
  const [bulkReasonDialogOpen, setBulkReasonDialogOpen] = useState(false);
  const [bulkTargetStatus, setBulkTargetStatus] = useState<string>('');
  const [bulkReason, setBulkReason] = useState('');
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  // Row actions menu (3-dot) state
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionsMenuEntry, setActionsMenuEntry] = useState<AllowListEntry | null>(null);

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<AllowListEntry | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Form states
  const [newEntry, setNewEntry] = useState<AllowListCreateRequest>({
    value: '',
    reason: ''
  });
  const [newValues, setNewValues] = useState<string[]>(['']);
  const [creatingEntry, setCreatingEntry] = useState(false);
  const [selectedTenantForEntry, setSelectedTenantForEntry] = useState<string>('');
  const [availableTenants, setAvailableTenants] = useState<any[]>([]);
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [updateEntry, setUpdateEntry] = useState<AllowListUpdateRequest>({
    status: 'approved',
    reason: ''
  });

  const currentTenantId = tenantId || selectedTenant?.tenant_id;

  // Debounce the free-text search box before it triggers a server-side request.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      let data: AllowListEntry[];
      if (isSuperUser) {
        // For super users, get all entries from all tenants
        data = await api.getAllAllowListEntries(
          statusFilter || undefined,
          typeFilter || undefined,
          search || undefined
        );
      } else {
        if (!currentTenantId) return;
        data = await api.getAllowListEntries(
          currentTenantId,
          statusFilter || undefined,
          typeFilter || undefined,
          search || undefined
        );
      }
      setEntries(data);
    } catch (error: any) {
      showNotification('Failed to load allow list entries', 'error');
      console.error('Error loading entries:', error);
    } finally {
      setLoading(false);
    }
  }, [currentTenantId, isSuperUser, statusFilter, typeFilter, search, showNotification]);

  const loadStats = useCallback(async () => {
    if (!currentTenantId && !isSuperUser) return;
    
    try {
      let data;
      if (isSuperUser) {
        // Load overall stats for super users
        data = await api.getOverallAllowListStats();
      } else if (currentTenantId) {
        // Load tenant-specific stats for regular users
        data = await api.getAllowListStats(currentTenantId);
      } else {
        return; // No tenant ID available
      }
      setStats(data);
    } catch (error: any) {
      console.error('Error loading stats:', error);
    }
  }, [currentTenantId, isSuperUser]);

  const loadTenants = useCallback(async () => {
    if (!isSuperUser) return;
    
    setLoadingTenants(true);
    try {
      console.log('Loading tenants for super user...');
      const tenants = await api.getTenants();
      console.log('Loaded tenants:', tenants);
      setAvailableTenants(tenants);
    } catch (error: any) {
      console.error('Error loading tenants:', error);
      showNotification('Failed to load tenants', 'error');
    } finally {
      setLoadingTenants(false);
    }
  }, [isSuperUser, showNotification]);

  useEffect(() => {
    if (isSuperUser) {
      loadEntries();
      loadTenants();
      loadStats();
    } else if (currentTenantId) {
      loadEntries();
      loadStats();
    }
  }, [currentTenantId, loadEntries, loadStats, isSuperUser, loadTenants]);

  // Load tenants for super users when component mounts
  useEffect(() => {
    if (isSuperUser) {
      loadTenants();
    }
  }, [isSuperUser, loadTenants]);

  const handleCreateEntry = async () => {
    const tenantIdToUse = isSuperUser ? selectedTenantForEntry : currentTenantId;
    
    if (!tenantIdToUse) {
      showNotification('Please select a tenant', 'error');
      return;
    }

    const targets = newValues.map(v => v.trim()).filter(v => v);
    if (targets.length === 0) {
      showNotification('Value is required', 'error');
      return;
    }
    
    try {
      setCreatingEntry(true);
      const result = await api.bulkCreateAllowListEntries(
        tenantIdToUse,
        targets,
        newEntry.reason?.trim() || undefined
      );
      const parts: string[] = [];
      if (result.created.length > 0) parts.push(`${result.created.length} added`);
      if (result.skipped.length > 0) parts.push(`${result.skipped.length} already existed`);
      if (result.failed.length > 0) parts.push(`${result.failed.length} failed`);
      showNotification(parts.join(', '), result.failed.length > 0 ? 'warning' : 'success');
      setCreateDialogOpen(false);
      setNewValues(['']);
      setNewEntry({ value: '', reason: '' });
      setSelectedTenantForEntry('');
      loadEntries();
      if (!isSuperUser) {
        loadStats();
      }
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Failed to create allow list entry', 'error');
    } finally {
      setCreatingEntry(false);
    }
  };

  const handleUpdateEntry = async () => {
    if (!selectedEntry) return;
    
    try {
      await api.updateAllowListEntry(selectedEntry.id, updateEntry);
      showNotification('Allow list entry updated successfully', 'success');
      setUpdateDialogOpen(false);
      setSelectedEntry(null);
      setUpdateEntry({ status: 'approved', reason: '' });
      loadEntries();
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Failed to update allow list entry', 'error');
    }
  };

  const handleRevokeEntry = async (entryId: string) => {
    if (!window.confirm('Are you sure you want to revoke this allow list entry? It can be requested again later.')) return;
    
    try {
      await api.updateAllowListEntry(entryId, { status: 'revoked', reason: 'Revoked by admin' });
      showNotification('Allow list entry revoked successfully', 'success');
      loadEntries();
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Failed to revoke allow list entry', 'error');
    }
  };

  const handleRequestAgain = async (entryId: string) => {
    if (!window.confirm('Are you sure you want to request this allow list entry again?')) return;
    
    try {
      await api.updateAllowListEntry(entryId, { status: 'requested', reason: 'Requested again by admin' });
      showNotification('Allow list entry requested again successfully', 'success');
      loadEntries();
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Failed to request allow list entry again', 'error');
    }
  };

  const handleBulkAction = (targetStatus: string) => {
    setBulkTargetStatus(targetStatus);
    setBulkReason('');
    setBulkReasonDialogOpen(true);
  };

  const handleBulkConfirm = async () => {
    if (selectedIds.size === 0) return;
    try {
      setBulkActing(true);
      setBulkReasonDialogOpen(false);
      const result = await api.bulkUpdateAllowListEntries(
        Array.from(selectedIds),
        bulkTargetStatus,
        bulkReason.trim() || undefined
      );
      const parts: string[] = [];
      if (result.updated.length > 0) parts.push(`${result.updated.length} updated`);
      if (result.failed.length > 0) parts.push(`${result.failed.length} failed`);
      showNotification(parts.join(', '), result.failed.length > 0 ? 'warning' : 'success');
      setSelectedIds(new Set());
      loadEntries();
      if (!isSuperUser) loadStats();
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Bulk update failed', 'error');
    } finally {
      setBulkActing(false);
    }
  };

  const closeActionsMenu = () => {
    setActionsMenuAnchor(null);
    setActionsMenuEntry(null);
  };

  const handleDeleteEntry = async () => {
    if (!entryToDelete) return;

    try {
      setDeleting(true);
      await api.deleteAllowListEntry(entryToDelete.id);
      showNotification(`Deleted allow list entry '${entryToDelete.value}'`, 'success');
      setDeleteDialogOpen(false);
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(entryToDelete.id);
        return next;
      });
      setEntryToDelete(null);
      loadEntries();
      if (!isSuperUser) loadStats();
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Failed to delete allow list entry', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedIds.size === 0) return;
    try {
      setBulkActing(true);
      setBulkDeleteDialogOpen(false);
      const result = await api.bulkDeleteAllowListEntries(Array.from(selectedIds));
      const parts: string[] = [];
      if (result.deleted.length > 0) parts.push(`${result.deleted.length} deleted`);
      if (result.failed.length > 0) parts.push(`${result.failed.length} failed`);
      showNotification(parts.join(', '), result.failed.length > 0 ? 'warning' : 'success');
      setSelectedIds(new Set());
      loadEntries();
      if (!isSuperUser) loadStats();
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Bulk delete failed', 'error');
    } finally {
      setBulkActing(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === entries.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(entries.map(e => e.id)));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <CheckIcon color="success" />;
      case 'denied':
        return <CloseIcon color="error" />;
      case 'requested':
        return <SecurityIcon color="warning" />;
      case 'revoked':
        return <CloseIcon color="disabled" />;
      default:
        return null;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'ip':
      case 'cidr':
        return <NetworkIcon />;
      case 'dns':
        return <DnsIcon />;
      case 'url':
        return <LinkIcon />;
      default:
        return null;
    }
  };

  const canCreateEntry = hasPermission('request_allow_list_entries');
  const canManageEntries = isSuperUser || hasPermission('manage_allow_list');
  
  // Additional check for tenant admins
  const isTenantAdmin = !isSuperUser && currentTenantId && hasPermission('manage_tenant_users');
  const shouldShowAddButton = canCreateEntry || isSuperUser || isTenantAdmin;
  
  // Enhanced management permissions for tenant admins
  const canManageEntriesEnhanced = canManageEntries || isTenantAdmin;
  
  // Debug logging
  console.log('AllowList Debug:', {
    isSuperUser,
    canCreateEntry,
    canManageEntries,
    canManageEntriesEnhanced,
    isTenantAdmin,
    shouldShowAddButton,
    hasPermission: hasPermission('request_allow_list_entries'),
    hasManagePermission: hasPermission('manage_allow_list'),
    hasTenantUserPermission: hasPermission('manage_tenant_users'),
    currentTenantId,
    entriesWithRequestAgain: entries.filter(entry => 
      (entry.status === 'revoked' || entry.status === 'denied') && 
      (canManageEntriesEnhanced || canCreateEntry)
    ).map(entry => ({ id: entry.id, status: entry.status, value: entry.value }))
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" component="h1">
            Allow List Management
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isSuperUser
              ? 'You can approve, deny, revoke, and re-request entries.'
              : 'You can request new targets and revoke or re-request existing ones. Only super users can approve or deny entries.'}
          </Typography>
        </Box>
        {shouldShowAddButton && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Add Entry
          </Button>
        )}
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {isSuperUser ? 'Overall Allow List Statistics' : 'Allow List Statistics'}
          </Typography>
        </Box>
      )}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Entries
                </Typography>
                <Typography variant="h4">
                  {stats.total_entries}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Approved
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.approved_entries}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Requested
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.requested_entries}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Denied
                </Typography>
                <Typography variant="h4" color="error.main">
                  {stats.denied_entries}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Revoked
                </Typography>
                <Typography variant="h4" color="text.secondary">
                  {stats.revoked_entries}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Search + filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          label="Search"
          placeholder={
            isSuperUser
              ? 'Search by value, reason, requested by, or tenant...'
              : 'Search by value, reason, or requested by...'
          }
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          helperText="Matches value, reason and requester (and tenant for super users). Combines with the filters below."
          inputProps={{ 'aria-label': 'Search allow list entries', 'data-testid': 'allow-list-search' }}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => setSearchInput('')}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FilterIcon />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="allow-list-status-filter-label">Status</InputLabel>
            <Select
              labelId="allow-list-status-filter-label"
              id="allow-list-status-filter"
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="denied">Denied</MenuItem>
              <MenuItem value="requested">Requested</MenuItem>
              <MenuItem value="revoked">Revoked</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="allow-list-type-filter-label">Type</InputLabel>
            <Select
              labelId="allow-list-type-filter-label"
              id="allow-list-type-filter"
              value={typeFilter}
              label="Type"
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="ip">IP Address</MenuItem>
              <MenuItem value="cidr">CIDR Range</MenuItem>
              <MenuItem value="dns">DNS Name</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Entries Table */}
      <Paper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6">Allow List Entries</Typography>
          {shouldShowAddButton && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              size="small"
            >
              Add Entry
            </Button>
          )}
        </Box>

        {/* Bulk action toolbar — visible when items are selected */}
        {selectedIds.size > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.5,
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
              {selectedIds.size} selected
            </Typography>
            {isSuperUser && (
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={bulkActing ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon />}
                disabled={bulkActing}
                onClick={() => handleBulkAction('approved')}
              >
                Approve All
              </Button>
            )}
            {isSuperUser && (
              <Button
                size="small"
                variant="contained"
                color="error"
                startIcon={bulkActing ? <CircularProgress size={14} color="inherit" /> : <CancelIcon />}
                disabled={bulkActing}
                onClick={() => handleBulkAction('denied')}
              >
                Deny All
              </Button>
            )}
            {canManageEntriesEnhanced && (
              <Button
                size="small"
                variant="outlined"
                color="warning"
                startIcon={bulkActing ? <CircularProgress size={14} color="inherit" /> : <BlockIcon />}
                disabled={bulkActing}
                onClick={() => handleBulkAction('revoked')}
              >
                Revoke All
              </Button>
            )}
            {canManageEntriesEnhanced && (
              <Button
                size="small"
                variant="outlined"
                startIcon={bulkActing ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
                disabled={bulkActing}
                onClick={() => handleBulkAction('requested')}
              >
                Re-request All
              </Button>
            )}
            {canManageEntriesEnhanced && (
              <Button
                size="small"
                variant="contained"
                color="error"
                startIcon={bulkActing ? <CircularProgress size={14} color="inherit" /> : <DeleteForeverIcon />}
                disabled={bulkActing}
                onClick={() => setBulkDeleteDialogOpen(true)}
              >
                Delete Selected
              </Button>
            )}
            <Button
              size="small"
              variant="text"
              onClick={() => setSelectedIds(new Set())}
              sx={{ ml: 'auto' }}
            >
              Clear
            </Button>
          </Box>
        )}

        {loading && <LinearProgress />}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {canManageEntriesEnhanced && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedIds.size > 0 && selectedIds.size < entries.length}
                      checked={entries.length > 0 && selectedIds.size === entries.length}
                      onChange={toggleSelectAll}
                      size="small"
                    />
                  </TableCell>
                )}
                <TableCell>Value</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Requested By</TableCell>
                {isSuperUser && <TableCell>Tenant</TableCell>}
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((entry) => (
                <TableRow
                  key={entry.id}
                  selected={selectedIds.has(entry.id)}
                  sx={{ cursor: canManageEntriesEnhanced ? 'pointer' : 'default' }}
                  onClick={canManageEntriesEnhanced ? () => toggleSelect(entry.id) : undefined}
                >
                  {canManageEntriesEnhanced && (
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.has(entry.id)}
                        onChange={() => toggleSelect(entry.id)}
                        size="small"
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getTypeIcon(entry.type)}
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {entry.value}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={entry.type.toUpperCase()}
                      color={ALLOW_LIST_TYPE_COLORS[entry.type as keyof typeof ALLOW_LIST_TYPE_COLORS] as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusIcon(entry.status)}
                      <Chip
                        label={entry.status}
                        color={ALLOW_LIST_STATUS_COLORS[entry.status as keyof typeof ALLOW_LIST_STATUS_COLORS] as any}
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>{entry.requested_by_name}</TableCell>
                  {isSuperUser && <TableCell>{entry.tenant_name}</TableCell>}
                  <TableCell>
                    {formatUiDateOnly(entry.created_at, timezone)}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {canManageEntriesEnhanced && (
                      <IconButton
                        size="small"
                        aria-label={`Actions for ${entry.value}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActionsMenuAnchor(e.currentTarget);
                          setActionsMenuEntry(entry);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Row actions menu (3-dot) */}
      <Menu
        anchorEl={actionsMenuAnchor}
        open={Boolean(actionsMenuAnchor)}
        onClose={closeActionsMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {actionsMenuEntry && (
          <>
            {actionsMenuEntry.status === 'requested' && isSuperUser && (
              <MenuItem
                onClick={() => {
                  setSelectedEntry(actionsMenuEntry);
                  setUpdateEntry({ status: 'approved', reason: '' });
                  setUpdateDialogOpen(true);
                  closeActionsMenu();
                }}
              >
                <ListItemIcon><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                <ListItemText>Approve</ListItemText>
              </MenuItem>
            )}
            {actionsMenuEntry.status === 'requested' && isSuperUser && (
              <MenuItem
                onClick={() => {
                  setSelectedEntry(actionsMenuEntry);
                  setUpdateEntry({ status: 'denied', reason: '' });
                  setUpdateDialogOpen(true);
                  closeActionsMenu();
                }}
              >
                <ListItemIcon><CloseIcon fontSize="small" color="error" /></ListItemIcon>
                <ListItemText>Deny</ListItemText>
              </MenuItem>
            )}
            {(actionsMenuEntry.status === 'revoked' || actionsMenuEntry.status === 'denied') &&
              (canManageEntriesEnhanced || canCreateEntry) && (
              <MenuItem
                onClick={() => {
                  const entryId = actionsMenuEntry.id;
                  closeActionsMenu();
                  handleRequestAgain(entryId);
                }}
              >
                <ListItemIcon><RefreshIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText>Request Again</ListItemText>
              </MenuItem>
            )}
            {actionsMenuEntry.status !== 'revoked' && (
              <MenuItem
                onClick={() => {
                  const entryId = actionsMenuEntry.id;
                  closeActionsMenu();
                  handleRevokeEntry(entryId);
                }}
              >
                <ListItemIcon><BlockIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText>Revoke</ListItemText>
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                setSelectedEntry(actionsMenuEntry);
                setUpdateEntry({ status: actionsMenuEntry.status, reason: actionsMenuEntry.reason || '' });
                setUpdateDialogOpen(true);
                closeActionsMenu();
              }}
            >
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Edit Status / Reason</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                setEntryToDelete(actionsMenuEntry);
                setDeleteDialogOpen(true);
                closeActionsMenu();
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon><DeleteForeverIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>Delete Permanently</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Bulk action reason dialog */}
      <Dialog open={bulkReasonDialogOpen} onClose={() => setBulkReasonDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {bulkTargetStatus === 'approved' && 'Approve Selected Entries'}
          {bulkTargetStatus === 'denied' && 'Deny Selected Entries'}
          {bulkTargetStatus === 'revoked' && 'Revoke Selected Entries'}
          {bulkTargetStatus === 'requested' && 'Re-request Selected Entries'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will update <strong>{selectedIds.size}</strong> entries to <strong>{bulkTargetStatus}</strong>.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Reason (Optional)"
            multiline
            rows={3}
            value={bulkReason}
            onChange={(e) => setBulkReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkReasonDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={bulkTargetStatus === 'approved' ? 'success' : bulkTargetStatus === 'denied' ? 'error' : 'primary'}
            onClick={handleBulkConfirm}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => { setCreateDialogOpen(false); setNewValues(['']); setNewEntry({ value: '', reason: '' }); setSelectedTenantForEntry(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Add Allow List Entry</DialogTitle>
        <DialogContent>
          {isSuperUser && (
            <FormControl fullWidth margin="dense">
              <InputLabel id="allow-list-tenant-label">Tenant</InputLabel>
              <Select
                labelId="allow-list-tenant-label"
                id="allow-list-tenant"
                value={selectedTenantForEntry}
                label="Tenant"
                onChange={(e) => setSelectedTenantForEntry(e.target.value)}
                required
              >
                {loadingTenants ? (
                  <MenuItem disabled>
                    <Typography color="text.secondary">Loading tenants...</Typography>
                  </MenuItem>
                ) : availableTenants.length === 0 ? (
                  <MenuItem disabled>
                    <Typography color="text.secondary">No tenants available</Typography>
                  </MenuItem>
                ) : (
                  availableTenants.map((tenant) => (
                    <MenuItem key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          )}
          {newValues.map((val, index) => (
            <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
              <TextField
                autoFocus={index === 0}
                fullWidth
                margin="dense"
                variant="outlined"
                label={index === 0 ? 'Value (IP, CIDR, or DNS)' : undefined}
                placeholder="IP, CIDR, or DNS name"
                helperText={index === 0 ? 'Enter an IP address (IPv4/IPv6), CIDR range, or DNS name' : undefined}
                value={val}
                onChange={(e) => {
                  const updated = [...newValues];
                  updated[index] = e.target.value;
                  setNewValues(updated);
                }}
                onPaste={(e) => {
                  const text = e.clipboardData.getData('text');
                  const lines = text.split(/[\n\r]+/).map(l => l.trim()).filter(l => l);
                  if (lines.length > 1) {
                    e.preventDefault();
                    const updated = [...newValues];
                    updated.splice(index, 1, ...lines);
                    setNewValues(updated);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const updated = [...newValues];
                    updated.splice(index + 1, 0, '');
                    setNewValues(updated);
                    setTimeout(() => {
                      const inputs = document.querySelectorAll<HTMLInputElement>('[data-allowlist-input]');
                      if (inputs[index + 1]) inputs[index + 1].focus();
                    }, 50);
                  }
                }}
                inputProps={{ 'data-allowlist-input': true, maxLength: 255 }}
              />
              {newValues.length > 1 && (
                <IconButton
                  size="small"
                  onClick={() => setNewValues(newValues.filter((_, i) => i !== index))}
                  sx={{ mt: index === 0 ? 1 : 0, color: 'error.main' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
              {index === newValues.length - 1 && (
                <IconButton
                  size="small"
                  onClick={() => setNewValues([...newValues, ''])}
                  sx={{ mt: index === 0 ? 1 : 0, color: 'primary.main' }}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
          <TextField
            margin="dense"
            label="Reason (Optional)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={newEntry.reason}
            onChange={(e) => setNewEntry({ ...newEntry, reason: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialogOpen(false);
            setNewValues(['']);
            setNewEntry({ value: '', reason: '' });
            setSelectedTenantForEntry('');
          }}>Cancel</Button>
          <Button
            onClick={handleCreateEntry}
            variant="contained"
            disabled={creatingEntry}
            startIcon={creatingEntry ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {creatingEntry
              ? `Creating${newValues.filter(v => v.trim()).length > 1 ? '...' : '...'}`
              : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Dialog - approve/deny only for super users */}
      <Dialog open={updateDialogOpen} onClose={() => setUpdateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Allow List Entry</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Value"
            fullWidth
            variant="outlined"
            value={selectedEntry?.value || ''}
            disabled
          />
          <FormControl fullWidth margin="dense">
            <InputLabel id="allow-list-update-status-label">Status</InputLabel>
            <Select
              labelId="allow-list-update-status-label"
              id="allow-list-update-status"
              value={updateEntry.status}
              label="Status"
              onChange={(e) => setUpdateEntry({ ...updateEntry, status: e.target.value as any })}
            >
              {isSuperUser && <MenuItem value="approved">Approved</MenuItem>}
              {isSuperUser && <MenuItem value="denied">Denied</MenuItem>}
              <MenuItem value="revoked">Revoked</MenuItem>
              <MenuItem value="requested">Requested</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Reason (Optional)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={updateEntry.reason}
            onChange={(e) => setUpdateEntry({ ...updateEntry, reason: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateEntry} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog (single entry) */}
      <Dialog open={deleteDialogOpen} onClose={() => { setDeleteDialogOpen(false); setEntryToDelete(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Allow List Entry</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Permanently delete <strong>{entryToDelete?.value}</strong> from the allow list? This cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This will also remove any discovered asset data for this entry. If a scenario or runbook still targets
            this value, it will no longer be authorized once the entry is gone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteDialogOpen(false); setEntryToDelete(null); }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteEntry}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog (bulk) */}
      <Dialog open={bulkDeleteDialogOpen} onClose={() => setBulkDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Selected Entries</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Permanently delete <strong>{selectedIds.size}</strong> {selectedIds.size === 1 ? 'entry' : 'entries'} from
            the allow list? This cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This will also remove any discovered asset data for these entries. If a scenario or runbook still
            targets one of these values, it will no longer be authorized once removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkDeleteConfirm}
            disabled={bulkActing}
            startIcon={bulkActing ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for mobile */}
      {shouldShowAddButton && (
        <Fab
          color="primary"
          aria-label="add entry"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'flex', sm: 'none' } // Only show on mobile
          }}
          onClick={() => setCreateDialogOpen(true)}
        >
          <AddIcon />
        </Fab>
      )}
    </Box>
  );
};

export default AllowList;
